import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Col, Container, Row } from "reactstrap";
import { useDocumentDataOnce } from "react-firebase-hooks/firestore";
import { IFestivalEvent } from "./services/Events";
import { firestore, storage } from "./firebase";
import { useDownloadURL } from "react-firebase-hooks/storage";

const FESTIVAL_EVENT_SLIDER_AUTOPLAY_INTERVAL_MS = 7000;

const FestivalEvent: React.FC<{ setName: (name: string) => void }> = ({
  setName,
}) => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event] = useDocumentDataOnce<IFestivalEvent>(
    firestore.doc(`/festivalevents2026/${eventId}`)
  );
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);

  const [imageUrl] = useDownloadURL(storage.ref(event?.picturePath));

  const galleryPaths = useMemo(
    () =>
      (event?.imageGallery || []).filter(
        (path): path is string =>
          typeof path === "string" && path.trim().length > 0
      ),
    [event?.imageGallery]
  );

  useEffect(() => {
    let isActive = true;

    if (galleryPaths.length === 0) {
      setGalleryUrls([]);
      setActiveGalleryIndex(0);
      return () => {
        isActive = false;
      };
    }

    const loadGalleryUrls = async () => {
      try {
        const urls = await Promise.all(
          galleryPaths.map(async (path) => {
            if (/^https?:\/\//i.test(path)) {
              return path;
            }

            if (path.startsWith("gs://")) {
              return storage.refFromURL(path).getDownloadURL();
            }

            return storage.ref(path).getDownloadURL();
          })
        );

        if (isActive) {
          setGalleryUrls(urls);
          setActiveGalleryIndex(0);
        }
      } catch (error) {
        console.error("Failed to load event image gallery", error);
        if (isActive) {
          setGalleryUrls([]);
          setActiveGalleryIndex(0);
        }
      }
    };

    loadGalleryUrls();

    return () => {
      isActive = false;
    };
  }, [galleryPaths]);

  setName(event?.title || "");

  const isEmptyOrSpaces = (str: string) =>
    str === null || str.match(/^ *$/) !== null;

  const hasGallery = galleryPaths.length > 0;
  const sliderImageUrls = hasGallery
    ? galleryUrls.length > 0
      ? galleryUrls
      : imageUrl
      ? [imageUrl]
      : []
    : [];

  const showPreviousGalleryImage = () => {
    if (sliderImageUrls.length <= 1) {
      return;
    }

    setActiveGalleryIndex((previousIndex) =>
      previousIndex === 0 ? sliderImageUrls.length - 1 : previousIndex - 1
    );
  };

  const showNextGalleryImage = () => {
    if (sliderImageUrls.length <= 1) {
      return;
    }

    setActiveGalleryIndex(
      (previousIndex) => (previousIndex + 1) % sliderImageUrls.length
    );
  };

  useEffect(() => {
    if (!hasGallery || sliderImageUrls.length <= 1) {
      return;
    }

    const autoplayInterval = window.setInterval(() => {
      setActiveGalleryIndex(
        (previousIndex) => (previousIndex + 1) % sliderImageUrls.length
      );
    }, FESTIVAL_EVENT_SLIDER_AUTOPLAY_INTERVAL_MS);

    return () => {
      window.clearInterval(autoplayInterval);
    };
  }, [hasGallery, sliderImageUrls.length]);

  useEffect(() => {
    if (sliderImageUrls.length > 0 && activeGalleryIndex >= sliderImageUrls.length) {
      setActiveGalleryIndex(0);
    }
  }, [activeGalleryIndex, sliderImageUrls.length]);

  return (
    <div className="festival-event-content">
      <Container>
        <Row>
          <Col sm="12">
            {/* <a href="#" onClick={() => history.push("/")}>
            {"<<<<"}
          </a> */}
          </Col>
          {/* <Col sm="12">
                <img src={imageUrl} style={{ width:"100%" }}></img>
               

            </Col> */}

          <Col sm="12">
            {/* <Card style={{cursor:"default" }} >
                <CardImg src={imageUrl} className="h-100" style={{objectFit: "scale-down", maxHeight:'400px'}} ></CardImg>
                { event && event.photoCopyright && <CardImgOverlay className=" h-100 p-0 d-flex flex-column justify-content-end"><CardText className=" copyright p-1"><strong> © {event.photoCopyright} </strong></CardText></CardImgOverlay>}
            </Card>
            <br/> */}

            {hasGallery ? (
              <div className="festival-event-slider">
                <div className="festival-event-slider-band">
                  <div
                    className="festival-event-slider-track"
                    style={{
                      transform: `translateX(calc(${activeGalleryIndex} * -1 * (var(--festival-event-slide-width) + var(--festival-event-slide-gap))))`,
                    }}
                  >
                    {sliderImageUrls.map((url, index) => (
                      <div
                        key={`${url}-${index}`}
                        className="festival-event-slide"
                      >
                        <img
                          className="image-responsive"
                          src={url}
                          alt={`${event?.title || "Festival event"} ${index + 1}`}
                        ></img>
                      </div>
                    ))}
                  </div>
                  {sliderImageUrls.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="festival-event-arrow festival-event-arrow-left"
                        aria-label="Vorheriges Bild"
                        onClick={showPreviousGalleryImage}
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="festival-event-arrow festival-event-arrow-right"
                        aria-label="Naechstes Bild"
                        onClick={showNextGalleryImage}
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>
                {sliderImageUrls.length > 1 && (
                  <div
                    className="festival-event-indicators"
                    role="tablist"
                    aria-label="Bildauswahl"
                  >
                    {sliderImageUrls.map((_, index) => (
                      <button
                        key={`indicator-${index}`}
                        type="button"
                        className={`festival-event-indicator ${
                          index === activeGalleryIndex ? "active" : ""
                        }`}
                        aria-label={`Bild ${index + 1} anzeigen`}
                        aria-current={
                          index === activeGalleryIndex ? "true" : undefined
                        }
                        onClick={() => setActiveGalleryIndex(index)}
                      ></button>
                    ))}
                  </div>
                )}
                {event?.photoCopyright && (
                  <p className="copyright"> © {event.photoCopyright}</p>
                )}
                <br />
              </div>
            ) : (
              <>
                <img
                  className="image-responsive"
                  src={imageUrl}
                  style={{
                    maxHeight: "500px",
                    width: "auto",
                    objectFit: "scale-down",
                    maxWidth: "90vw",
                  }}
                  alt={event?.title || "Festival event"}
                ></img>
                {event?.photoCopyright && (
                  <p className="copyright"> © {event.photoCopyright}</p>
                )}
                <br />
              </>
            )}

            {/* <img src={member?.pictureUrl} style={{height:"290px", width:"250px", objectFit: "cover", marginTop:"30px", marginBottom:"30px" }}></img> */}
          </Col>
        </Row>
        <Row className="festival-modal-content">
          <Col sm="12">
            <h3>{event?.title}</h3>
            {event && event.teaser && !isEmptyOrSpaces(event.teaser) && (
              <div
                className="subtitle"
                dangerouslySetInnerHTML={{ __html: event.teaser }}
              ></div>
            )}
          </Col>
          <Col sm="12">
            <h5>
              {event?.start.toDate().toLocaleDateString("de-DE")}{" "}
              {event?.start.toDate().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
            </h5>
          </Col>

          <Col sm="12">
            <div
              dangerouslySetInnerHTML={{
                __html: event ? event.description : "",
              }}
            ></div>
            {event?.linkUrl && (
              <p>
                <a onClick={() => window.open(event?.linkUrl)} href="#">
                  {event?.linkText ? event?.linkText : event.linkUrl}
                </a>
              </p>
            )}
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FestivalEvent;
